import { useMemo } from 'react';
import { useAuth } from '../utils/AuthContext';
import { useCRM } from '../utils/CRMContext';
import { resolveEmployeeDesignations } from '../utils/contractorPayablesAccess';
import {
  canApproveInstallmentPayments,
  canManageBillingPayables,
  canMarkReadyToBill,
  canViewBillingPayables,
} from '../utils/billingPayablesAccess';

/** Billing & Payables hub access with CRM hydration fallback for designations. */
export function useBillingPayablesAccess() {
  const { user } = useAuth();
  const { crmUsers } = useCRM();

  const designations = useMemo(
    () => resolveEmployeeDesignations(user, crmUsers),
    [crmUsers, user],
  );

  const briefingProfile = useMemo(() => {
    const fromAuth = user?.executiveBriefing;
    const fromCrm = crmUsers.find((u) => u.uid === user?.id)?.executiveBriefing;
    const cfg = fromAuth?.enabled ? fromAuth : fromCrm?.enabled ? fromCrm : undefined;
    if (!cfg?.enabled) return undefined;
    // User Management defaults an enabled briefing with no profile to COO.
    if (!cfg.profile || cfg.profile === 'coo') return 'coo' as const;
    return cfg.profile;
  }, [crmUsers, user]);

  const accessArgs = useMemo(
    () => ({ authRole: user?.role, designations, briefingProfile }),
    [user?.role, designations, briefingProfile],
  );

  const canManage = canManageBillingPayables(accessArgs);
  const canMarkReady = canMarkReadyToBill(accessArgs);
  const canApprove = canApproveInstallmentPayments(accessArgs);
  const canView = canViewBillingPayables(accessArgs);

  return { canManage, canMarkReady, canApprove, canView, designations };
}
